import { useEffect, useRef, useState } from 'react';
import { DropEvent, FileRejection } from 'react-dropzone';

import { postMedia } from '@common/api/media';

import FileWithProgress from '../types/fileWithProgress';

enum FileUploadErrorsCodes {
	LargeFile = 'file-too-large',
	InvalidType = 'file-invalid-type',
}

type PropsType = {
	multiple: boolean;
	value: any;
	onChange: (newValue: any) => void;
	onFileUpload?: (newValue?: File) => Promise<any> | any;
};

const useFileDrop = ({
	multiple,
	value,
	onChange,
	onFileUpload,
}: PropsType) => {
	const [filesOnProgress, setFilesOnProgress] = useState<FileWithProgress[]>([]);
	const [isDragOver, setIsDragOver] = useState(false);
	const [errorType, setErrorType] = useState<string | null>(null);
	const valueRef = useRef(value);

	useEffect(() => {
		valueRef.current = value;
	}, [value]);

	const uploadFile = async (fileForUpload: FileWithProgress) => {
		if (!fileForUpload.file) return;
		let response;

		if (onFileUpload) {
			response = await onFileUpload(fileForUpload?.file);
		} else {
			response = await postMedia(fileForUpload?.file, (progress: number) =>
				setFilesOnProgress((prevFiles) =>
					prevFiles.map((f) => (f.file === fileForUpload.file ? { ...f, progress } : f)),
				),
			);
		}

		const responseFile = response?.file ?? response;

		if (responseFile?.url) {
			setFilesOnProgress((prevFiles) => prevFiles.filter((f) => f.file !== fileForUpload.file));
			const currentValue = valueRef.current;
			let currentList: any[] = [];
			if (Array.isArray(currentValue)) {
				currentList = currentValue;
			} else if (currentValue) {
				currentList = [currentValue];
			}
			const nextValue = multiple ? [...currentList, responseFile] : responseFile;
			valueRef.current = nextValue;
			onChange(nextValue);
			return;
		}

		if (onFileUpload) {
			setFilesOnProgress((prevFiles) => prevFiles.filter((f) => f.file !== fileForUpload.file));
		}
	};

	const onDrop = (acceptedFiles: File[]) => {
		setErrorType(null);
		const newFiles = acceptedFiles.map((file) => ({
			file,
			progress: 0,
			name: file.name,
		}));
		setFilesOnProgress((prevFiles) => [...prevFiles, ...newFiles]);
		newFiles.forEach((fileWithProgress) => {
			uploadFile(fileWithProgress);
		});
	};

	const onReject = (
		rejectedFiles: FileRejection[],
		event: DropEvent,
		maxSize?: number,
	) => {
		const [file] = rejectedFiles;
		const errorCode = file?.errors?.[0].code;

		if (maxSize && errorCode === FileUploadErrorsCodes.LargeFile) setErrorType(`File is more than ${maxSize}mb`);
		else if (errorCode === FileUploadErrorsCodes.InvalidType) setErrorType(`${file.file.type} type is not supported.`);
		else setErrorType(`${file?.errors?.[0]?.message}`);

		setIsDragOver(false);
	};

	const handleDeleteOnProgress = (fileOrUrl: File | string) => {
		setFilesOnProgress(
			(prevFiles) => prevFiles
				.filter((f) => f.file !== fileOrUrl && f.url !== fileOrUrl),
		);
	};

	const onDragEnter = () => {
		setIsDragOver(true);
	};

	const onDragLeave = () => {
		setIsDragOver(false);
	};

	return { // @TODO: Check what files should be displayed
		onDrop,
		handleDeleteOnProgress,
		isDragOver,
		onDragEnter,
		onDragLeave,
		filesOnProgress,
		onReject,
		errorType,
	};
};

export default useFileDrop;
